import React, { Component } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
    IconButton, InputAdornment, Typography, Divider, CircularProgress
} from '@material-ui/core';
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import CloseIcon from '@material-ui/icons/Close';
import LockIcon from '@material-ui/icons/Lock';
import { injectIntl } from 'react-intl';
import PasswordStrengthInput from '../password-strength-input';
import * as userService from '../../services/user.service';

class ChangePasswordDialog extends Component {
    state = {
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
        newPasswordValid: false,
        showCurrent: false,
        showConfirm: false,
        submitting: false,
        error: '',
        success: false,
    };

    componentDidUpdate(prevProps) {
        if (!prevProps.open && this.props.open) {
            this.setState({
                currentPassword: '',
                newPassword: '',
                confirmPassword: '',
                newPasswordValid: false,
                showCurrent: false,
                showConfirm: false,
                submitting: false,
                error: '',
                success: false,
            });
        }
    }

    handleField = (field) => (e) => this.setState({ [field]: e.target.value, error: '' });

    canSubmit = () => {
        const { currentPassword, newPassword, confirmPassword, newPasswordValid, submitting } = this.state;
        if (submitting) return false;
        if (!currentPassword) return false;
        if (!newPasswordValid) return false;
        if (newPassword !== confirmPassword) return false;
        if (newPassword === currentPassword) return false;
        return true;
    }

    handleSubmit = () => {
        const { intl, onClose, onSuccess } = this.props;
        const { currentPassword, newPassword } = this.state;
        this.setState({ submitting: true, error: '' });
        userService.changePassword(currentPassword, newPassword)
            .then(result => {
                if (result?.data?.success) {
                    this.setState({ submitting: false, success: true });
                    if (onSuccess) onSuccess(intl.formatMessage({ id: 'change.password.success' }));
                    setTimeout(() => { if (onClose) onClose(); }, 1200);
                } else {
                    const msg = result?.data?.errors?.[0] || intl.formatMessage({ id: 'change.password.error' });
                    this.setState({ submitting: false, error: msg });
                }
            })
            .catch(err => {
                const msg = err?.response?.data?.errors?.[0] || intl.formatMessage({ id: 'change.password.error' });
                this.setState({ submitting: false, error: msg });
            });
    }

    render() {
        const { open, onClose, intl } = this.props;
        const { currentPassword, newPassword, confirmPassword, showCurrent, showConfirm, submitting, error, success } = this.state;
        const mismatch = confirmPassword.length > 0 && newPassword !== confirmPassword;
        const sameAsCurrent = newPassword.length > 0 && newPassword === currentPassword;

        return (
            <Dialog open={open} onClose={submitting ? null : onClose} maxWidth="sm" fullWidth
                PaperProps={{ style: { borderRadius: 12, overflow: 'hidden' } }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 24px', backgroundColor: '#f5f7fa' }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <LockIcon style={{ color: '#277afe', marginRight: 12 }} />
                        <Typography variant="h6" style={{ fontWeight: 600 }}>
                            {intl.formatMessage({ id: 'change.password' })}
                        </Typography>
                    </div>
                    <IconButton size="small" onClick={onClose} disabled={submitting}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </div>
                <Divider />
                <DialogContent style={{ padding: 24 }}>
                    <TextField
                        label={intl.formatMessage({ id: 'change.password.current' })}
                        type={showCurrent ? 'text' : 'password'}
                        value={currentPassword}
                        onChange={this.handleField('currentPassword')}
                        fullWidth
                        required
                        autoComplete="current-password"
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton size="small" tabIndex={-1} onClick={() => this.setState(s => ({ showCurrent: !s.showCurrent }))}>
                                        {showCurrent ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                        style={{ marginBottom: 20 }}
                    />

                    <PasswordStrengthInput
                        label={intl.formatMessage({ id: 'change.password.new' })}
                        value={newPassword}
                        onChange={this.handleField('newPassword')}
                        onValidityChange={(v) => this.setState({ newPasswordValid: v })}
                        required
                    />

                    <TextField
                        label={intl.formatMessage({ id: 'change.password.confirm' })}
                        type={showConfirm ? 'text' : 'password'}
                        value={confirmPassword}
                        onChange={this.handleField('confirmPassword')}
                        fullWidth
                        required
                        error={mismatch}
                        helperText={mismatch ? intl.formatMessage({ id: 'change.password.mismatch' }) : ''}
                        autoComplete="new-password"
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton size="small" tabIndex={-1} onClick={() => this.setState(s => ({ showConfirm: !s.showConfirm }))}>
                                        {showConfirm ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                        style={{ marginTop: 20 }}
                    />

                    {sameAsCurrent && (
                        <Typography variant="caption" style={{ color: '#d32f2f', display: 'block', marginTop: 8 }}>
                            {intl.formatMessage({ id: 'change.password.same.as.current' })}
                        </Typography>
                    )}

                    {error && (
                        <div style={{ marginTop: 16, padding: '10px 14px', borderRadius: 6, backgroundColor: '#ffebee', borderLeft: '4px solid #b71c1c', color: '#b71c1c', fontSize: 13 }}>
                            ⚠ {error}
                        </div>
                    )}
                    {success && (
                        <div style={{ marginTop: 16, padding: '10px 14px', borderRadius: 6, backgroundColor: '#e8f5e9', borderLeft: '4px solid #2e7d32', color: '#2e7d32', fontSize: 13 }}>
                            ✓ {intl.formatMessage({ id: 'change.password.success' })}
                        </div>
                    )}
                </DialogContent>
                <DialogActions style={{ padding: '12px 24px 16px', backgroundColor: '#f5f5f5', borderTop: '1px solid #e0e0e0' }}>
                    <Button onClick={onClose} disabled={submitting} style={{ textTransform: 'none' }}>
                        {intl.formatMessage({ id: 'cancel' })}
                    </Button>
                    <Button
                        onClick={this.handleSubmit}
                        disabled={!this.canSubmit()}
                        variant="contained"
                        color="primary"
                        startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
                        style={{ textTransform: 'none', fontWeight: 600 }}
                    >
                        {intl.formatMessage({ id: 'save' })}
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }
}

export default injectIntl(ChangePasswordDialog);
